import { useState, useEffect } from 'react';
import api from '../../services/api';

export default function EventForm({ event, initialDate, initialPeriode, users = [], onSave, onCancel, onDelete }) {
    const [form, setForm] = useState({
        titre: '',
        date: initialDate || '',
        periode: initialPeriode || 'matin',
        heureDebut: '',
        description: '',
        livreurs: [],
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (event) {
            setForm({
                titre: event.titre || '',
                date: event.date?.substring(0, 10) || '',
                periode: event.periode || 'matin',
                heureDebut: event.heureDebut ? event.heureDebut.substring(11, 16) : '',
                description: event.description || '',
                // Les livreurs peuvent être des IRIs (strings) ou des objets
                livreurs: (event.livreurs || []).map(l =>
                    typeof l === 'string' ? l : (l['@id'] || `/api/users/${l.id}`)
                ),
            });
        }
    }, [event]);

    const toggleLivreur = (iri) => {
        setForm(f => ({
            ...f,
            livreurs: f.livreurs.includes(iri)
                ? f.livreurs.filter(l => l !== iri)
                : [...f.livreurs, iri],
        }));
    };

    const buildPayload = () => ({
        titre: form.titre,
        date: form.date,
        periode: form.periode,
        heureDebut: form.heureDebut ? `1970-01-01T${form.heureDebut}:00+00:00` : null,
        description: form.description || null,
        livreurs: form.livreurs,
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (event) {
                // PATCH avec ld+json (pas merge-patch) pour que les collections soient remplacées
                const id = event.id || event['@id']?.split('/').pop();
                await api.patch(`/events/${id}`, buildPayload(), {
                    headers: { 'Content-Type': 'application/ld+json' },
                });
            } else {
                await api.post('/events', buildPayload(), {
                    headers: { 'Content-Type': 'application/ld+json' },
                });
            }
            onSave && onSave();
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!event) return;
        setLoading(true);
        try {
            const id = event.id || event['@id']?.split('/').pop();
            await api.delete(`/events/${id}`);
            onDelete && onDelete();
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <div style={fieldStyle}>
                <label style={labelStyle}>Titre *</label>
                <input
                    type="text"
                    value={form.titre}
                    onChange={e => setForm({ ...form, titre: e.target.value })}
                    style={inputStyle}
                    placeholder="Réunion, rétrocession CHU, formation…"
                    required
                    autoFocus
                />
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Date *</label>
                    <input
                        type="date"
                        value={form.date}
                        onChange={e => setForm({ ...form, date: e.target.value })}
                        style={inputStyle}
                        required
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Période *</label>
                    <select
                        value={form.periode}
                        onChange={e => setForm({ ...form, periode: e.target.value })}
                        style={inputStyle}
                    >
                        <option value="matin">🌅 Matin</option>
                        <option value="aprem">🌇 Après-midi</option>
                    </select>
                </div>
            </div>

            <div style={fieldStyle}>
                <label style={labelStyle}>Heure (optionnelle)</label>
                <input
                    type="time"
                    value={form.heureDebut}
                    onChange={e => setForm({ ...form, heureDebut: e.target.value })}
                    style={inputStyle}
                />
            </div>

            <div style={fieldStyle}>
                <label style={labelStyle}>Description (optionnelle)</label>
                <textarea
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                    rows={2}
                    placeholder="Détails, lieu, informations…"
                    style={{ ...inputStyle, resize: 'none', fontFamily: 'inherit' }}
                />
            </div>

            {users.length > 0 && (
                <div style={fieldStyle}>
                    <label style={labelStyle}>Livreurs concernés (optionnel)</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {users.map(u => {
                            const iri = u['@id'] || `/api/users/${u.id}`;
                            const selected = form.livreurs.includes(iri);
                            return (
                                <div
                                    key={iri}
                                    onClick={() => toggleLivreur(iri)}
                                    style={{
                                        padding: '5px 12px',
                                        borderRadius: '20px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        border: selected ? '2px solid #0ea5e9' : '2px solid #e2e8f0',
                                        backgroundColor: selected ? '#e0f2fe' : '#f8fafc',
                                        color: selected ? '#0369a1' : '#64748b',
                                        transition: 'all 0.15s',
                                        userSelect: 'none',
                                    }}
                                >
                                    {selected ? '✓ ' : ''}{u.nom || u.email}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '8px' }}>
                <div>
                    {event && (
                        <button type="button" onClick={handleDelete} disabled={loading}
                                style={{ padding: '10px 16px', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                            🗑️ Supprimer
                        </button>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button type="button" onClick={onCancel} style={cancelButtonStyle}>Annuler</button>
                    <button type="submit" disabled={loading} style={submitButtonStyle}>
                        {loading ? 'Enregistrement…' : event ? 'Modifier' : 'Créer'}
                    </button>
                </div>
            </div>
        </form>
    );
}

const fieldStyle = { marginBottom: '16px' };
const labelStyle = { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#444' };
const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box', backgroundColor: 'white' };
const cancelButtonStyle = { padding: '10px 20px', backgroundColor: '#f0f2f5', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' };
const submitButtonStyle = { padding: '10px 20px', backgroundColor: '#0ea5e9', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' };
